import { useEffect, useState } from 'react';
import { useContracts } from './useContracts';

// Traverses the team tree via BFS to compute all user IDs at a given level.
// - Only runs when `enabled` is true (so opening the modal triggers the load).
// - Bounded: at most FANOUT_CAP nodes expanded per level (protects against
//   very wide branches on public RPCs). Truncated flag is returned so the UI
//   can inform the user.
// - After finding IDs at target level, fetches each user's stored record
//   (capped at SHOW_LIMIT for display cost).
const FANOUT_CAP = 600;   // max nodes expanded at any single depth
const SHOW_LIMIT = 200;   // max user records fetched for display

export const useLevelMembers = (rootId, targetLevel, enabled) => {
  const c = useContracts();
  const [state, setState] = useState({
    loading: false,
    users: [],
    totalAtLevel: 0,
    truncatedLevels: false,
    truncatedDisplay: false,
  });

  useEffect(() => {
    if (!enabled) return;
    if (!rootId || rootId === 0n || !targetLevel || targetLevel < 1) return;

    let cancelled = false;
    (async () => {
      setState({ loading: true, users: [], totalAtLevel: 0, truncatedLevels: false, truncatedDisplay: false });
      try {
        let current = [BigInt(rootId)];
        let truncatedLevels = false;

        for (let d = 1; d <= Number(targetLevel); d++) {
          if (current.length === 0) break;

          if (current.length > FANOUT_CAP) {
            truncatedLevels = true;
            current = current.slice(0, FANOUT_CAP);
          }

          const batches = await Promise.all(
            current.map((id) => c.read.store.getDirects(id).catch(() => []))
          );
          if (cancelled) return;
          const next = [];
          for (const b of batches) for (const x of b) next.push(BigInt(x));
          current = next;
        }

        const totalAtLevel = current.length;
        const truncatedDisplay = totalAtLevel > SHOW_LIMIT;
        const shown = current.slice(0, SHOW_LIMIT);

        const users = await Promise.all(shown.map(async (id) => {
          try {
            const u = await c.read.store.getUser(id);
            return {
              id,
              wallet: u.wallet,
              totalInvestmentUSDT: u.totalInvestmentUSDT,
              activeInvestmentUSDT: u.activeInvestmentUSDT,
              rank: u.rank,
              directReferrals: u.directReferrals,
              active: u.active,
              blacklisted: u.blacklisted,
            };
          } catch { return null; }
        }));

        if (cancelled) return;
        setState({
          loading: false,
          users: users.filter(Boolean),
          totalAtLevel,
          truncatedLevels,
          truncatedDisplay,
        });
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, users: [], totalAtLevel: 0, truncatedLevels: false, truncatedDisplay: false, error: e?.message });
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootId, targetLevel, enabled]);

  return state;
};
